<template>
  <div>
    <v-row v-if="showSaveButton" class="mr-3 ml-3">
      <v-col class="ml-auto" md="auto">
        <v-btn color="success" @click="save" :loading="loading"> Save </v-btn>
      </v-col>
    </v-row>

    <v-simple-table :class="{ 'mt-4': showSaveButton }">
      <template v-slot:default>
        <thead>
          <tr>
            <th class="text-left" width="150">Day</th>
            <th class="text-left" width="150">{{ statusLabel }}</th>
            <th class="text-left">Time</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(day, index) in weekDays" :key="day.value">
            <td>{{ day.text }}</td>
            <td>
              <v-switch
                v-model="daySettings[day.value].isOpen"
                :label="daySettings[day.value].isOpen ? activeLabel : inactiveLabel"
                color="success"
                dense
                hide-details
                @change="onScheduleChange"
              />
            </td>
            <td>
              <div v-if="daySettings[day.value].isOpen" class="d-flex align-center">
                <v-menu
                  ref="startMenu"
                  v-model="daySettings[day.value].startMenu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  max-width="290px"
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="daySettings[day.value].startDisplay"
                      label="Start Time"
                      prepend-icon="mdi-clock-time-four-outline"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      hide-details
                      dense
                      outlined
                      class="mr-2"
                      style="max-width: 180px"
                    ></v-text-field>
                  </template>
                  <v-time-picker
                    v-model="daySettings[day.value].startTime"
                    format="24hr"
                    @input="onStartTimeChange(day.value)"
                  ></v-time-picker>
                </v-menu>

                <span class="mx-2">to</span>

                <v-menu
                  ref="endMenu"
                  v-model="daySettings[day.value].endMenu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  max-width="290px"
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="daySettings[day.value].endDisplay"
                      label="End Time"
                      prepend-icon="mdi-clock-time-four-outline"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      hide-details
                      dense
                      outlined
                      style="max-width: 180px"
                    ></v-text-field>
                  </template>
                  <v-time-picker
                    v-model="daySettings[day.value].endTime"
                    format="24hr"
                    :min="daySettings[day.value].startTime"
                    @input="onEndTimeChange(day.value)"
                  ></v-time-picker>
                </v-menu>
              </div>
              <div v-else>
                <v-chip small>{{ inactiveChipLabel }}</v-chip>
              </div>
            </td>
          </tr>
          <!-- Break everyday row -->
          <tr v-if="enableBreakSetting">
            <td>Break everyday</td>
            <td>
              <v-switch
                v-model="breakSettings.isEnabled"
                :label="breakSettings.isEnabled ? 'Active' : 'Inactive'"
                color="warning"
                dense
                hide-details
                @change="onBreakChange"
              />
            </td>
            <td>
              <div v-if="breakSettings.isEnabled" class="d-flex align-center">
                <v-menu
                  v-model="breakSettings.startMenu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  max-width="290px"
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="breakSettings.startDisplay"
                      label="Break Start"
                      prepend-icon="mdi-clock-time-four-outline"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      hide-details
                      dense
                      outlined
                      class="mr-2"
                      style="max-width: 180px"
                    ></v-text-field>
                  </template>
                  <v-time-picker
                    v-model="breakSettings.startTime"
                    format="24hr"
                    @input="onBreakStartTimeChange"
                  ></v-time-picker>
                </v-menu>

                <span class="mx-2">to</span>

                <v-menu
                  v-model="breakSettings.endMenu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  max-width="290px"
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="breakSettings.endDisplay"
                      label="Break End"
                      prepend-icon="mdi-clock-time-four-outline"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      hide-details
                      dense
                      outlined
                      style="max-width: 180px"
                    ></v-text-field>
                  </template>
                  <v-time-picker
                    v-model="breakSettings.endTime"
                    format="24hr"
                    :min="breakSettings.startTime"
                    @input="onBreakEndTimeChange"
                  ></v-time-picker>
                </v-menu>
              </div>
              <div v-else>
                <v-chip small color="grey">No break</v-chip>
              </div>
            </td>
          </tr>
        </tbody>
      </template>
    </v-simple-table>
  </div>
</template>

<script>
import { mapActions } from 'vuex';

export default {
  name: 'WeekSchedule',
  props: {
    openHours: {
      type: Array,
      default: () => [],
    },
    breakHours: {
      type: Array,
      default: () => [],
    },
    // For clinic opening hours, pass the clinicId to enable save functionality
    clinicId: {
      type: String,
      default: null,
    },
    // Customization props
    statusLabel: {
      type: String,
      default: 'Status',
    },
    activeLabel: {
      type: String,
      default: 'Open',
    },
    inactiveLabel: {
      type: String,
      default: 'Closed',
    },
    inactiveChipLabel: {
      type: String,
      default: 'Closed',
    },
    enableBreakSetting: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      weekDays: [
        { text: 'Monday', value: 1 },
        { text: 'Tuesday', value: 2 },
        { text: 'Wednesday', value: 3 },
        { text: 'Thursday', value: 4 },
        { text: 'Friday', value: 5 },
        { text: 'Saturday', value: 6 },
        { text: 'Sunday', value: 7 },
      ],
      daySettings: {
        1: {
          isOpen: false,
          startTime: '09:00',
          endTime: '18:00',
          startDisplay: '09:00',
          endDisplay: '18:00',
          startMenu: false,
          endMenu: false,
        },
        2: {
          isOpen: false,
          startTime: '09:00',
          endTime: '18:00',
          startDisplay: '09:00',
          endDisplay: '18:00',
          startMenu: false,
          endMenu: false,
        },
        3: {
          isOpen: false,
          startTime: '09:00',
          endTime: '18:00',
          startDisplay: '09:00',
          endDisplay: '18:00',
          startMenu: false,
          endMenu: false,
        },
        4: {
          isOpen: false,
          startTime: '09:00',
          endTime: '18:00',
          startDisplay: '09:00',
          endDisplay: '18:00',
          startMenu: false,
          endMenu: false,
        },
        5: {
          isOpen: false,
          startTime: '09:00',
          endTime: '18:00',
          startDisplay: '09:00',
          endDisplay: '18:00',
          startMenu: false,
          endMenu: false,
        },
        6: {
          isOpen: false,
          startTime: '09:00',
          endTime: '18:00',
          startDisplay: '09:00',
          endDisplay: '18:00',
          startMenu: false,
          endMenu: false,
        },
        7: {
          isOpen: false,
          startTime: '09:00',
          endTime: '18:00',
          startDisplay: '09:00',
          endDisplay: '18:00',
          startMenu: false,
          endMenu: false,
        },
      },
      breakSettings: {
        isEnabled: false,
        startTime: '12:00',
        endTime: '13:00',
        startDisplay: '12:00',
        endDisplay: '13:00',
        startMenu: false,
        endMenu: false,
      },
    };
  },
  computed: {
    showSaveButton() {
      // Only show save button if clinicId is provided (for clinic opening hours)
      return !!this.clinicId;
    },
  },
  watch: {
    openHours: {
      immediate: true,
      handler(newVal) {
        this.parseOpenHours(newVal);
      },
    },
    breakHours: {
      immediate: true,
      handler(newVal) {
        this.parseBreakHours(newVal);
      },
    },
  },
  methods: {
    ...mapActions('alert', ['updateAlertMessage']),

    parseOpenHours(hours) {
      // Reset all days to closed
      Object.keys(this.daySettings).forEach(day => {
        this.daySettings[day].isOpen = false;
        // Keep default times
        this.daySettings[day].startTime = '09:00';
        this.daySettings[day].endTime = '18:00';
        this.daySettings[day].startDisplay = '09:00';
        this.daySettings[day].endDisplay = '18:00';
      });

      // Parse existing open hours
      if (hours && Array.isArray(hours)) {
        hours.forEach(hour => {
          if (hour.isoDayOfWeek && this.daySettings[hour.isoDayOfWeek]) {
            this.daySettings[hour.isoDayOfWeek].isOpen = true;
            this.daySettings[hour.isoDayOfWeek].startTime = this.minutesToTime(hour.start);
            this.daySettings[hour.isoDayOfWeek].endTime = this.minutesToTime(hour.end);
            this.daySettings[hour.isoDayOfWeek].startDisplay = this.minutesToTime(hour.start);
            this.daySettings[hour.isoDayOfWeek].endDisplay = this.minutesToTime(hour.end);
          }
        });
      }
    },

    parseBreakHours(hours) {
      // Reset break settings
      this.breakSettings.isEnabled = false;
      this.breakSettings.startTime = '12:00';
      this.breakSettings.endTime = '13:00';
      this.breakSettings.startDisplay = '12:00';
      this.breakSettings.endDisplay = '13:00';

      // If break hours exist, enable and set the time from the first entry
      // (all days should have the same break time)
      if (hours && Array.isArray(hours) && hours.length > 0) {
        this.breakSettings.isEnabled = true;
        const firstBreak = hours[0];
        this.breakSettings.startTime = this.minutesToTime(firstBreak.start);
        this.breakSettings.endTime = this.minutesToTime(firstBreak.end);
        this.breakSettings.startDisplay = this.minutesToTime(firstBreak.start);
        this.breakSettings.endDisplay = this.minutesToTime(firstBreak.end);
      }
    },

    minutesToTime(minutes) {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
    },

    timeToMinutes(timeStr) {
      const [hours, minutes] = timeStr.split(':').map(Number);
      return hours * 60 + minutes;
    },

    onStartTimeChange(dayValue) {
      this.daySettings[dayValue].startDisplay = this.daySettings[dayValue].startTime;
      this.daySettings[dayValue].startMenu = false;
      this.onScheduleChange();
    },

    onEndTimeChange(dayValue) {
      this.daySettings[dayValue].endDisplay = this.daySettings[dayValue].endTime;
      this.daySettings[dayValue].endMenu = false;
      this.onScheduleChange();
    },

    onScheduleChange() {
      // Emit the update immediately for components that don't have save button
      if (!this.showSaveButton) {
        this.emitUpdate();
      }
    },

    onBreakChange() {
      if (!this.showSaveButton) {
        this.emitBreakUpdate();
      }
    },

    onBreakStartTimeChange() {
      this.breakSettings.startDisplay = this.breakSettings.startTime;
      this.breakSettings.startMenu = false;
      this.onBreakChange();
    },

    onBreakEndTimeChange() {
      this.breakSettings.endDisplay = this.breakSettings.endTime;
      this.breakSettings.endMenu = false;
      this.onBreakChange();
    },

    emitBreakUpdate() {
      // Convert breakSettings to breakHours format
      const breakHours = [];
      if (this.breakSettings.isEnabled) {
        // Create break hours for all 7 days
        for (let day = 1; day <= 7; day++) {
          breakHours.push({
            isoDayOfWeek: day,
            start: this.timeToMinutes(this.breakSettings.startTime),
            end: this.timeToMinutes(this.breakSettings.endTime),
          });
        }
      }

      // Emit the update event to parent
      this.$emit('update-breaks', breakHours);
    },

    emitUpdate() {
      // Convert daySettings to openHours format
      const openHours = [];
      Object.keys(this.daySettings).forEach(day => {
        if (this.daySettings[day].isOpen) {
          openHours.push({
            isoDayOfWeek: parseInt(day),
            start: this.timeToMinutes(this.daySettings[day].startTime),
            end: this.timeToMinutes(this.daySettings[day].endTime),
          });
        }
      });

      // Emit the update event to parent
      this.$emit('update', openHours);
    },

    async save() {
      try {
        this.loading = true;

        // Convert daySettings to openHours format
        const openHours = [];
        Object.keys(this.daySettings).forEach(day => {
          if (this.daySettings[day].isOpen) {
            openHours.push({
              isoDayOfWeek: parseInt(day),
              start: this.timeToMinutes(this.daySettings[day].startTime),
              end: this.timeToMinutes(this.daySettings[day].endTime),
            });
          }
        });

        // Convert breakSettings to breakHours format
        const breakHours = [];
        if (this.breakSettings.isEnabled) {
          // Create break hours for all 7 days
          for (let day = 1; day <= 7; day++) {
            breakHours.push({
              isoDayOfWeek: day,
              start: this.timeToMinutes(this.breakSettings.startTime),
              end: this.timeToMinutes(this.breakSettings.endTime),
            });
          }
        }

        // Emit the update events to parent
        this.$emit('update-breaks', breakHours);
        this.$emit('update', openHours);

        await this.updateAlertMessage({
          msg: 'Schedule updated successfully',
          type: 'success',
          color: 'success',
        });
      } catch (error) {
        console.error('Error saving schedule:', error);
        await this.updateAlertMessage({
          msg: 'Failed to update schedule',
          type: 'error',
        });
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.v-simple-table {
  margin: 20px;
}
</style>
